import { Loader2, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Renames a questionnaire from the list.
 *
 * A REAL DIALOG, not `window.prompt` — which is what the reference uses. A native prompt is
 * unstyled, cannot be given a max length or a disabled-while-saving state, and is blocked outright
 * by some browsers, so the menu item would silently do nothing.
 */
export function RenameQuestionnaireDialog({
  open,
  currentName,
  isPending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  currentName: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState(currentName);

  // Re-seeded each time the dialog opens on a different row, otherwise the second rename starts
  // with the first row's name already typed in.
  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  if (!open) return null;

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentName && !isPending;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
            <Pencil className="h-4 w-4 text-primary" />
          </span>
          <h3 className="text-base font-semibold text-black-logo">Rename questionnaire</h3>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-[length:var(--text-14)] text-black">Name</span>
          <input
            type="text"
            value={name}
            maxLength={200}
            // Enter saves and Escape closes — a one-field dialog where the keyboard does not work is
            // slower than the menu item that opened it.
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSave) onConfirm(trimmed);
              if (e.key === 'Escape') onCancel();
            }}
            // biome-ignore lint/a11y/noAutofocus: a single-field dialog opened deliberately from a menu item — the field is the only thing to do here.
            autoFocus
            className="min-h-[42px] w-full rounded-lg border border-placeholder bg-white px-4 py-2.5 text-[length:var(--text-14)] text-[#323338] outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-lg border border-grayEF px-4 py-2 text-[length:var(--text-14)] font-medium text-[#374151] transition-colors hover:bg-[#F9FAFB] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canSave && onConfirm(trimmed)}
            disabled={!canSave}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-white transition-colors hover:bg-[#0038CC] disabled:opacity-50"
          >
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
